'use client';

import React from 'react';

import { Vehicle } from '@/types/used-cars/used-cars.types';

interface CarListSchemaProps {
  vehicles: Vehicle[];
  pageTitle?: string;
  pageDescription?: string;
}

const CarListSchema: React.FC<CarListSchemaProps> = ({ 
  vehicles, 
  pageTitle, 
  pageDescription 
}) => {
  const generateCarListSchema = () => {
    const carListings = vehicles.slice(0, 20).map(vehicle => ({
      '@type': 'Car',
      name: vehicle.title || `${vehicle.brand} ${vehicle.model}`,
      model: vehicle.model,
      brand: {
        '@type': 'Brand',
        name: vehicle.brand,
      },
      vehicleModelDate: vehicle.year,
      bodyType: vehicle.bodyType,
      fuelType: vehicle.fuelType,
      numberOfDoors: parseInt(vehicle.doors) || undefined,
      vehicleTransmission: vehicle.transmission,
      driveWheelConfiguration: vehicle.drivetrain,
      mileageFromOdometer: {
        '@type': 'QuantitativeValue',
        value: parseInt(String(vehicle.mileage).replace(/[^0-9]/g, '')) || 0,
        unitCode: 'KM',
        unitText: 'Kilometers',
      },
      color: vehicle.bodyColors,
      offers: {
        '@type': 'Offer',
        price: vehicle.price ? vehicle.price.replace(/[^0-9.]/g, '') : undefined,
        priceCurrency: 'ZAR',
        availability: vehicle.special ? 'https://schema.org/SaleOnSale' : 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/UsedCondition',
        seller: {
          '@type': 'AutoDealer',
          name: vehicle.dealership_id?.name || 'Kia South Africa',
          address: vehicle.dealership_id?.address ? {
            '@type': 'PostalAddress',
            streetAddress: vehicle.dealership_id.address,
            addressLocality: vehicle.dealership_id?.city || '',
            addressRegion: vehicle.dealership_id?.province || '',
            postalCode: vehicle.dealership_id?.postal_code || '',
            addressCountry: 'ZA',
          } : undefined,
        },
        validFrom: vehicle.sale_end_date ? undefined : new Date().toISOString(),
        validThrough: vehicle.sale_end_date || undefined,
      },
      image: vehicle.mainImage ? [vehicle.mainImage] : [],
      url: vehicle.link || `https://www.kia.co.za/used-cars/${vehicle.brand?.toLowerCase()}/${vehicle.model?.toLowerCase()}/${vehicle.id}`,
    }));

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: pageTitle || 'Used Cars for Sale - Kia South Africa',
      description:
        pageDescription ||
        'View a wide range of Used Cars For Sale available from Kia Group in South Africa.',
      itemListElement: carListings.map((car, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: car,
      })),
    };

    // Remove undefined properties
    const cleanSchema = JSON.parse(JSON.stringify(schema, (key, value) => {
      if (value === undefined || value === null || value === '') {
        return undefined;
      }
      if (Array.isArray(value) && value.length === 0) {
        return undefined;
      }
      return value;
    }));

    return cleanSchema;
  };

  const schema = generateCarListSchema();

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema, null, 2),
      }}
    />
  );
};

export default CarListSchema;